import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { map, takeUntil } from 'rxjs/operators';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { LookbackPeriodType } from 'src/core/models/generic-lookup-type/query/lookback-period-type.glt';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { AutoUnsubscribe } from 'src/core/decorators/auto-unsubscribe.decorator';
import { HasSubscription } from 'src/ca-shared/ca-shared.module';
import { LicensingService } from 'src/core/services/licensing/licensing-service';
import { LicenseInfoDto } from 'src/core/models/administration/licensing/license-info.dto';

@Component({
  selector: 'ca-genai-feature-settings',
  templateUrl: './genai-feature-settings.component.html',
  styleUrls: ['./genai-feature-settings.component.scss'],
  standalone: false,
})
@AutoUnsubscribe()
export class GenAIFeatureSettingsComponent extends HasSubscription implements OnInit {
  genAIFeatureSettingsForm: FormGroup;
  genAIFeatureSettingDtos: ConfigurationSettingsDto[] = [];
  lookbackPeriodOptions: GenericLookupDto[] = [];
  isSaving = false;
  licenseInfo: LicenseInfoDto;
  defaultLookbackPeriodTypeCode: string;

  private readonly settingKeyAIInsightsDailyLimit = 'GenAI.AIInsightsDailyLimit';
  private readonly settingKeyAskGenAIMonthlyLimit = 'GenAI.AskGenAIMonthlyLimit';
  private readonly settingKeyAICoachingMonthlyAgentLimit = 'GenAI.AICoachingMonthlyAgentLimit';
  private readonly settingKeyQueryWithGenAIMonthlyLimit = 'GenAI.QueryWithGenAIMonthlyLimit';
  private readonly settingKeyQueryWithGenAILookbackPeriod = 'GenAI.QueryWithGenAILookbackPeriod';
  private readonly settingKeyAutoTagLimit = 'GenAI.AutoTagLimit';

  constructor(
    private config: ConfigStateService,
    private fb: FormBuilder,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private store: Store,
    private localizationService: LocalizationService,
    private licensingService: LicensingService,
  ) {
    super();

    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(
        map(filterFn => filterFn(LookbackPeriodType)),
        takeUntil(this.autoUnsubscribeNotifier),
      )
      .subscribe(result => {
        this.lookbackPeriodOptions = result.filter(g => g.id != LookbackPeriodType.customInterval);
        this.defaultLookbackPeriodTypeCode = this.lookbackPeriodOptions.find(
          f => f.id === LookbackPeriodType.lastOneMonth,
        ).code;
      });

    this.genAIFeatureSettingsForm = this.fb.group({
      aiInsightsDailyLimit: [
        Number(this.config.getSetting(this.settingKeyAIInsightsDailyLimit)),
        { validators: [Validators.min(0)] },
      ],
      askGenAIMonthlyLimit: [
        Number(this.config.getSetting(this.settingKeyAskGenAIMonthlyLimit)),
        { validators: [Validators.min(0)] },
      ],
      aiCoachingMonthlyAgentLimit: [
        Number(this.config.getSetting(this.settingKeyAICoachingMonthlyAgentLimit)),
        { validators: [Validators.min(0)] },
      ],
      queryWithGenAIMonthlyLimit: [
        Number(this.config.getSetting(this.settingKeyQueryWithGenAIMonthlyLimit)),
        { validators: [Validators.min(0)] },
      ],
      queryWithGenAILookbackPeriod: [
        this.config.getSetting(this.settingKeyQueryWithGenAILookbackPeriod) ??
          this.defaultLookbackPeriodTypeCode,
      ],
      autoTagLimit: [
        Number(this.config.getSetting(this.settingKeyAutoTagLimit)),
        { validators: [Validators.min(0)] },
      ],
    });
  }

  ngOnInit(): void {
    this.licensingService.getLicenseInfo('ca-agent').subscribe(data => {
      this.licenseInfo = data;
    });
  }

  trackByCode(index: number, option: GenericLookupDto): string {
    return option.code;
  }

  onSubmitGenAIFeatureSettings(): void {
    if (this.genAIFeatureSettingsForm.invalid) {
      return;
    }

    this.genAIFeatureSettingDtos = new Array();

    this.genAIFeatureSettingDtos.push({
      settingName: this.settingKeyAIInsightsDailyLimit,
      settingValue: this.genAIFeatureSettingsForm.get('aiInsightsDailyLimit').value?.toString(),
    });

    this.genAIFeatureSettingDtos.push({
      settingName: this.settingKeyAskGenAIMonthlyLimit,
      settingValue: this.genAIFeatureSettingsForm.get('askGenAIMonthlyLimit').value?.toString(),
    });

    this.genAIFeatureSettingDtos.push({
      settingName: this.settingKeyAICoachingMonthlyAgentLimit,
      settingValue: this.genAIFeatureSettingsForm
        .get('aiCoachingMonthlyAgentLimit')
        .value?.toString(),
    });

    this.genAIFeatureSettingDtos.push({
      settingName: this.settingKeyQueryWithGenAIMonthlyLimit,
      settingValue: this.genAIFeatureSettingsForm
        .get('queryWithGenAIMonthlyLimit')
        .value?.toString(),
    });

    this.genAIFeatureSettingDtos.push({
      settingName: this.settingKeyQueryWithGenAILookbackPeriod,
      settingValue: this.genAIFeatureSettingsForm
        .get('queryWithGenAILookbackPeriod')
        .value?.toString(),
    });

    this.genAIFeatureSettingDtos.push({
      settingName: this.settingKeyAutoTagLimit,
      settingValue: this.genAIFeatureSettingsForm.get('autoTagLimit').value?.toString(),
    });

    this.isSaving = true;
    this.settingService
      .saveSetting(this.genAIFeatureSettingDtos)
      .pipe(takeUntil(this.autoUnsubscribeNotifier))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.toastr.success(
            this.localizationService.instant('AbpSettingManagement::SavedSuccessfully'),
          );
        },
        error: () => {
          this.isSaving = false;
          this.toastr.error(
            this.localizationService.instant('AbpSettingManagement::DefaultErrorMessage'),
          );
        },
      });
  }
}
